import React, { useEffect, useRef, lazy, Suspense } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, useNavigate, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { motion } from "framer-motion";
import Lenis from "lenis";
import { LangProvider } from "@/i18n";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import api, { setToken } from "@/lib/api";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import ArtifactWatermark from "@/components/ArtifactWatermark";
import AmbientMusic from "@/components/AmbientMusic";
import ErrorBoundary from "@/components/ErrorBoundary";
import PWAInstallBanner from "@/components/PWAInstallBanner";
import WhatsAppChatButton from "@/components/WhatsAppChatButton";
import { PageLoader } from "@/components/Skeleton";

import Home from "@/pages/Home";
const History = lazy(() => import("@/pages/History"));
const PeriodDetail = lazy(() => import("@/pages/PeriodDetail"));
const Peoples = lazy(() => import("@/pages/Peoples"));
const PeopleDetail = lazy(() => import("@/pages/PeopleDetail"));
const MapPage = lazy(() => import("@/pages/MapPage"));
const TreePage = lazy(() => import("@/pages/TreePage"));
const Timeline = lazy(() => import("@/pages/Timeline"));
const Figures = lazy(() => import("@/pages/Figures"));
const Culture = lazy(() => import("@/pages/Culture"));
const Tales = lazy(() => import("@/pages/Tales"));
const Videos = lazy(() => import("@/pages/Videos"));
const Organigramme = lazy(() => import("@/pages/Organigramme"));
const Trombinoscope = lazy(() => import("@/pages/Trombinoscope"));
const Jeunesse = lazy(() => import("@/pages/Jeunesse"));
const Langues = lazy(() => import("@/pages/Langues"));
const Manifeste = lazy(() => import("@/pages/Manifeste"));
const Frise = lazy(() => import("@/pages/Frise"));
const Bibliographie = lazy(() => import("@/pages/Bibliographie"));
const Projects = lazy(() => import("@/pages/Projects"));
const Album = lazy(() => import("@/pages/Album"));
const Don = lazy(() => import("@/pages/Don"));
const Events = lazy(() => import("@/pages/Events"));
const Galleries = lazy(() => import("@/pages/Galleries"));
const Membership = lazy(() => import("@/pages/Membership"));
const MemberDashboard = lazy(() => import("@/pages/MemberDashboard"));
const Admin = lazy(() => import("@/pages/Admin"));
const Login = lazy(() => import("@/pages/Login"));
const CardVerify = lazy(() => import("@/pages/CardVerify"));
const About = lazy(() => import("@/pages/Static").then((m) => ({ default: m.About })));
const Governance = lazy(() => import("@/pages/Static").then((m) => ({ default: m.Governance })));
const Contact = lazy(() => import("@/pages/Static").then((m) => ({ default: m.Contact })));

const Shell = ({ children }) => {
  const { pathname } = useLocation();
  return (
    <div className="min-h-screen flex flex-col bg-ab-black relative ab-grain">
      <ArtifactWatermark />
      <AmbientMusic />
      <Nav />
      <main className="flex-1">
        <ErrorBoundary>
          <motion.div
            key={pathname}
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, ease: [0.32, 0.72, 0, 1] }}
          >
            <Suspense fallback={<PageLoader />}>{children}</Suspense>
          </motion.div>
        </ErrorBoundary>
      </main>
      <Footer />
      <WhatsAppChatButton />
      <PWAInstallBanner />
    </div>
  );
};

const ProtectedRoute = ({ children, admin }) => {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-ab-gold">…</div>;
  if (!user) return <Navigate to="/connexion" replace />;
  if (admin && !(user.role === "admin" || user.role === "super_admin")) return <Navigate to="/" replace />;
  return children;
};

const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);
  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;
    const hash = window.location.hash;
    const sid = new URLSearchParams(hash.replace("#", "")).get("session_id");
    if (!sid) { navigate("/connexion"); return; }
    (async () => {
      try {
        const res = await api.post("/auth/session", {}, { headers: { "X-Session-ID": sid } });
        setToken(res.data.token);
        window.location.href = "/espace-membre";
      } catch (e) {
        navigate("/connexion");
      }
    })();
  }, [navigate]);
  return <div className="min-h-screen flex items-center justify-center bg-ab-black text-ab-gold">Connexion…</div>;
};

const AppRouter = () => {
  const location = useLocation();
  const lenisRef = useRef(null);

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return undefined;
    const lenis = new Lenis({ duration: 1.05, smoothWheel: true });
    lenisRef.current = lenis;
    let raf;
    const loop = (time) => { lenis.raf(time); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); lenisRef.current = null; };
  }, []);

  useEffect(() => {
    if (lenisRef.current) lenisRef.current.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [location.pathname]);

  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={<Shell><Home /></Shell>} />
      <Route path="/histoire" element={<Shell><History /></Shell>} />
      <Route path="/histoire/:slug" element={<Shell><PeriodDetail /></Shell>} />
      <Route path="/peuples" element={<Shell><Peoples /></Shell>} />
      <Route path="/peuples/:slug" element={<Shell><PeopleDetail /></Shell>} />
      <Route path="/carte" element={<Shell><MapPage /></Shell>} />
      <Route path="/arbre" element={<Shell><TreePage /></Shell>} />
      <Route path="/chronologie" element={<Shell><Timeline /></Shell>} />
      <Route path="/figures" element={<Shell><Figures /></Shell>} />
      <Route path="/culture" element={<Shell><Culture /></Shell>} />
      <Route path="/contes" element={<Shell><Tales /></Shell>} />
      <Route path="/videos" element={<Shell><Videos /></Shell>} />
      <Route path="/organigramme" element={<Shell><Organigramme /></Shell>} />
      <Route path="/communaute" element={<Shell><Trombinoscope /></Shell>} />
      <Route path="/jeunesse" element={<Shell><Jeunesse /></Shell>} />
      <Route path="/langues" element={<Shell><Langues /></Shell>} />
      <Route path="/manifeste" element={<Shell><Manifeste /></Shell>} />
      <Route path="/frise" element={<Shell><Frise /></Shell>} />
      <Route path="/bibliographie" element={<Shell><Bibliographie /></Shell>} />
      <Route path="/projets" element={<Shell><Projects /></Shell>} />
      <Route path="/album" element={<Shell><Album /></Shell>} />
      <Route path="/don" element={<Shell><Don /></Shell>} />
      <Route path="/evenements" element={<Shell><Events /></Shell>} />
      <Route path="/galeries" element={<Shell><Galleries /></Shell>} />
      <Route path="/a-propos" element={<Shell><About /></Shell>} />
      <Route path="/gouvernance" element={<Shell><Governance /></Shell>} />
      <Route path="/contact" element={<Shell><Contact /></Shell>} />
      <Route path="/connexion" element={<Shell><Login /></Shell>} />
      <Route path="/verifier/:cardNumber" element={<Shell><CardVerify /></Shell>} />
      <Route path="/adhesion" element={<ProtectedRoute><Shell><Membership /></Shell></ProtectedRoute>} />
      <Route path="/espace-membre" element={<ProtectedRoute><Shell><MemberDashboard /></Shell></ProtectedRoute>} />
      <Route path="/admin" element={<ProtectedRoute admin><Shell><Admin /></Shell></ProtectedRoute>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <LangProvider>
      <AuthProvider>
        <BrowserRouter>
          <Toaster position="top-center" theme="dark" richColors />
          <AppRouter />
        </BrowserRouter>
      </AuthProvider>
    </LangProvider>
  );
}

export default App;
